import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ApartmentOutlined, SearchOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, Input, Spin, Empty, Avatar, Tag } from 'antd';
import { useNotify } from '@/hooks';
import TimeTrackingService from '@/services/timeTracking.service';
import { useProjectStructure } from '../../_shared/hooks';

const UNASSIGNED_KEY = '__unassigned__';

function getInitials(firstName, lastName) {
  return `${(firstName || '')[0] || ''}${(lastName || '')[0] || ''}`.toUpperCase();
}

function AssigneeCard({ person, isDraggable, onDragStart, t }) {
  const handleDragStart = (e) => {
    e.dataTransfer.setData('text/plain', person._id);
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.5';
    if (onDragStart) onDragStart(person._id);
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
  };

  const isLead = person.role === 'lead';

  return (
    <div
      draggable={isDraggable}
      onDragStart={isDraggable ? handleDragStart : undefined}
      onDragEnd={isDraggable ? handleDragEnd : undefined}
      className={`mb-1.5 flex items-center gap-2 rounded-lg border bg-white px-2.5 py-1.5 shadow-sm transition-shadow hover:shadow ${
        isDraggable ? 'cursor-grab active:cursor-grabbing' : ''
      } ${isLead ? 'border-l-4 border-l-accent' : ''}`}
    >
      <Avatar size={28} className="shrink-0 !bg-accent text-xs !text-white">
        {getInitials(person.firstName, person.lastName)}
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-xs font-medium text-zinc-700">
            {person.firstName} {person.lastName}
          </span>
          {isLead && (
            <Tag
              color="blue"
              className="!m-0 !px-1.5 !py-0.5 !text-[10px] !leading-none"
            >
              {t('lead', { ns: 'forms' })}
            </Tag>
          )}
        </div>
      </div>
    </div>
  );
}

function TaskDropZone({
  projectId,
  task,
  isUnassigned,
  searchValue,
  dragOverKey,
  onDragOver,
  onDragLeave,
  onDrop,
  t,
}) {
  const dropKey = `${projectId}::${isUnassigned ? UNASSIGNED_KEY : task._id}`;
  const isDragOver = dragOverKey === dropKey;
  const people = isUnassigned ? task : task.assignees || [];

  const search = searchValue.toLowerCase();
  const filtered = people.filter((p) => {
    if (!search) return true;
    const fullName = `${p.firstName} ${p.lastName}`.toLowerCase();
    return fullName.includes(search) || p.email?.toLowerCase().includes(search);
  });

  return (
    <div
      className={`rounded-lg border px-2.5 py-2 transition-colors ${
        isUnassigned
          ? 'border-orange-200 bg-orange-50/40'
          : 'border-zinc-100 bg-white/60'
      } ${isDragOver ? '!border-dashed !border-accent !bg-accent/5' : ''}`}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        onDragOver(dropKey);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          onDragLeave();
        }
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDrop(e, dropKey);
      }}
    >
      <div className="mb-1.5 flex items-center justify-between">
        <span
          className={`text-xs font-semibold ${
            isUnassigned ? 'text-orange-600' : 'text-zinc-600'
          }`}
        >
          {isUnassigned ? t('unassignedMembers', { ns: 'forms' }) : task.name}
        </span>
        <span
          className={`flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-medium ${
            isUnassigned
              ? 'bg-orange-200 text-orange-700'
              : 'bg-zinc-200 text-zinc-500'
          }`}
        >
          {filtered.length}
        </span>
      </div>
      <div className="max-h-[200px] overflow-y-auto">
        {filtered.map((person) => (
          <AssigneeCard key={person._id} person={person} isDraggable t={t} />
        ))}
        {filtered.length === 0 && (
          <div className="py-2 text-center text-[10px] text-gray-400">
            {search ? t('noData', { ns: 'tables' }) : '—'}
          </div>
        )}
      </div>
    </div>
  );
}

function ProjectColumn({
  project,
  searchValue,
  dragOverKey,
  onDragOver,
  onDragLeave,
  onDrop,
  t,
}) {
  return (
    <div className="flex h-full min-h-0 min-w-[280px] max-w-[340px] shrink-0 flex-col rounded-xl border border-zinc-200 bg-gray-50/50">
      {/* Project header */}
      <div className="flex items-center gap-2 rounded-t-xl bg-gray-100/80 px-4 py-3">
        <span
          className="inline-block h-3.5 w-3.5 shrink-0 rounded-full"
          style={{ backgroundColor: project.color }}
        />
        <span className="truncate text-sm font-semibold text-zinc-700">
          {project.name}
        </span>
        {project.code && (
          <span className="shrink-0 text-xs text-gray-400">{project.code}</span>
        )}
        <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-zinc-200 px-1.5 text-xs font-medium text-zinc-600">
          {project.memberCount || 0}
        </span>
      </div>

      {/* Tasks + Unassigned */}
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-3">
        {(project.tasks || []).map((task) => (
          <TaskDropZone
            key={task._id}
            projectId={project._id}
            task={task}
            isUnassigned={false}
            searchValue={searchValue}
            dragOverKey={dragOverKey}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            t={t}
          />
        ))}
        <TaskDropZone
          projectId={project._id}
          task={project.unassigned || []}
          isUnassigned
          searchValue={searchValue}
          dragOverKey={dragOverKey}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          t={t}
        />
      </div>
    </div>
  );
}

export default function ProjectStructureModal({ companyId, open, onClose }) {
  const { t } = useTranslation(['forms', 'menus', 'messages', 'tables']);
  const notification = useNotify();
  const queryClient = useQueryClient();

  const [searchValue, setSearchValue] = useState('');
  const [pendingChanges, setPendingChanges] = useState({});
  const [localStructure, setLocalStructure] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);

  const { data, isLoading } = useProjectStructure(companyId, open);

  useEffect(() => {
    if (data && Object.keys(pendingChanges).length === 0) {
      setLocalStructure(data);
    }
  }, [data, pendingChanges]);

  const structure = localStructure || data;

  const { mutateAsync, isPending: isSaving } = useMutation({
    mutationFn: (payload) => TimeTrackingService.reassignTaskMembers(payload),
    onSuccess: async (response) => {
      setPendingChanges({});
      setLocalStructure(null);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['time-tracking', 'project-structure'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['time-tracking', 'projects'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['time-tracking', 'project'],
        }),
      ]);
      notification.success({
        message: t('success', { ns: 'messages' }),
        description: response.message,
      });
      onClose();
    },
    onError: (error) => {
      notification.error({
        message: t('error', { ns: 'messages' }),
        description: error.response?.data?.message || error.message,
      });
    },
  });

  const handleSave = async () => {
    const assignments = Object.values(pendingChanges);
    if (assignments.length > 0) {
      await mutateAsync({ assignments });
    }
  };

  const handleCancel = () => {
    setPendingChanges({});
    setLocalStructure(null);
    setSearchValue('');
    onClose();
  };

  const handleDrop = useCallback((e, targetDropKey) => {
    setDragOverKey(null);
    const dragKey = e.dataTransfer.getData('text/plain');
    if (!dragKey || !targetDropKey) return;

    const [targetProjectId, targetTaskId] = targetDropKey.split('::');

    setLocalStructure((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));

      let draggedPerson = null;
      let fromProjectId = null;
      let fromTaskId = null;

      // Search through all projects/tasks/unassigned to find the dragged person
      for (const project of next.projects) {
        // Check tasks
        for (const task of project.tasks || []) {
          const idx = (task.assignees || []).findIndex(
            (a) => a._id.toString() === dragKey
          );
          if (idx !== -1) {
            [draggedPerson] = task.assignees.splice(idx, 1);
            fromProjectId = project._id;
            fromTaskId = task._id;
            break;
          }
        }
        if (draggedPerson) break;

        // Check unassigned
        const uIdx = (project.unassigned || []).findIndex(
          (a) => a._id.toString() === dragKey
        );
        if (uIdx !== -1) {
          [draggedPerson] = project.unassigned.splice(uIdx, 1);
          fromProjectId = project._id;
          fromTaskId = null;
          break;
        }
      }

      if (!draggedPerson) return prev;

      // Don't record change if dropped in the same spot
      if (
        fromProjectId === targetProjectId &&
        ((fromTaskId === null && targetTaskId === UNASSIGNED_KEY) ||
          fromTaskId === targetTaskId)
      ) {
        // Put it back
        const srcProject = next.projects.find(
          (p) => p._id.toString() === fromProjectId
        );
        if (fromTaskId) {
          const srcTask = srcProject.tasks.find(
            (t) => t._id.toString() === fromTaskId
          );
          srcTask.assignees.push(draggedPerson);
        } else {
          srcProject.unassigned.push(draggedPerson);
        }
        return prev;
      }

      // Add to target
      const targetProject = next.projects.find(
        (p) => p._id.toString() === targetProjectId
      );
      if (!targetProject) return prev;

      if (targetTaskId === UNASSIGNED_KEY) {
        targetProject.unassigned = targetProject.unassigned || [];
        targetProject.unassigned.push(draggedPerson);
      } else {
        const targetTask = targetProject.tasks.find(
          (t) => t._id.toString() === targetTaskId
        );
        if (!targetTask) return prev;
        targetTask.assignees = targetTask.assignees || [];
        targetTask.assignees.push(draggedPerson);
      }

      // Record pending change
      setPendingChanges((p) => ({
        ...p,
        [dragKey]: {
          employeeId: dragKey,
          fromProjectId,
          fromTaskId: fromTaskId || null,
          toProjectId: targetProjectId,
          toTaskId: targetTaskId === UNASSIGNED_KEY ? null : targetTaskId,
        },
      }));

      return next;
    });
  }, []);

  const hasPendingChanges = Object.keys(pendingChanges).length > 0;
  const changeCount = Object.keys(pendingChanges).length;

  return (
    <Modal
      title={
        <div className="flex items-center justify-between pr-8">
          <span className="flex items-center gap-2">
            <ApartmentOutlined />
            {t('projectStructure', { ns: 'forms' })}
          </span>
          <Input
            prefix={<SearchOutlined className="text-gray-400" />}
            placeholder={t('searchMembers', { ns: 'forms' })}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            allowClear
            className="!w-64"
          />
        </div>
      }
      open={open}
      onCancel={handleCancel}
      centered
      width="90vw"
      styles={{ body: { height: '75vh', overflow: 'hidden' } }}
      footer={
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400">
            {t('dragMemberHint', { ns: 'forms' })}
          </span>
          <div className="flex gap-2">
            <button className="btn-secondary" onClick={handleCancel}>
              {t('close', { ns: 'menus' })}
            </button>
            <button
              className="btn-primary text-white disabled:opacity-60"
              onClick={handleSave}
              disabled={!hasPendingChanges || isSaving}
            >
              {t('saveChanges', { ns: 'menus' })}
              {hasPendingChanges && ` (${changeCount})`}
            </button>
          </div>
        </div>
      }
      destroyOnClose
    >
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <Spin size="large" />
        </div>
      ) : !structure || (structure.projects || []).length === 0 ? (
        <Empty description={t('noData', { ns: 'tables' })} />
      ) : (
        <div className="flex h-full gap-4 overflow-x-auto pb-2">
          {(structure.projects || []).map((project) => (
            <ProjectColumn
              key={project._id}
              project={project}
              searchValue={searchValue}
              dragOverKey={dragOverKey}
              onDragOver={setDragOverKey}
              onDragLeave={() => setDragOverKey(null)}
              onDrop={handleDrop}
              t={t}
            />
          ))}
        </div>
      )}
    </Modal>
  );
}
